import * as THREE from "three";
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import FontFaceObserver from 'fontfaceobserver';
import { getFontFamily } from "utils/functions.utils";
import { IObject, ObjectTypes } from "utils/interface.utils";
import { updateObjectInTargetImage, setIsUpdatingFromEditorWorkspace } from "store/reducers/editorSlice";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";
import { renderTransformBox } from "components/editor_workspace/scene/transform_box.helper";

export const createObjectButton = async (workspace: WorkspaceContext, objectID: string, properties: IObject) => {
    const { rendererRef, sceneRef, cameraRef, loaderRef, targetImageRef, objectsRef } = workspace.refs;
    const { dispatch, selectedTargetImageID } = workspace;
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current || !loaderRef.current) return;

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    const targetImageGroup = targetImageRef.current;

    try {
        const objectButton = await renderObjectButton(scene, properties, objectID, targetImageGroup)

        if (objectButton && objectButton.mesh) {
            objectsRef.current.set(
                objectID,
                {
                    mesh: objectButton.mesh,
                    cleanup: objectButton.cleanup
                }
            )

            dispatch(updateObjectInTargetImage({
                targetImageID: selectedTargetImageID ?? '',
                object: {
                    id: objectID,
                    type: ObjectTypes.BUTTON,
                    properties: {
                        ...properties,
                        scaleX: objectButton.mesh.userData.originalWidth,
                        scaleY: objectButton.mesh.userData.originalHeight
                    }
                },
                skipHistory: true
            }))
            dispatch(setIsUpdatingFromEditorWorkspace(true))
            renderer.render(scene, camera);
        } else {
            console.log("Object button was not created successfully");
        }

    } catch (error) {
        console.log("Error creating object button:", error);
    }
}

export const updateObjectButton = async (workspace: WorkspaceContext, objectID: string, properties: IObject) => {
    const { rendererRef, sceneRef, cameraRef, dragSystemRef, rotationSystemRef, scaleSystemRef, targetImageRef, objectsRef, selectedTransformControlRef } = workspace.refs;
    const { dispatch, selectedTargetImageID, selectedObjectID } = workspace;
    if (dragSystemRef.current?.isObjectBeingDraggedAndPositioned(objectID)) {
        console.log(`Skipping text update for ${objectID} - currently being dragged`);
        return;
    }

    if (rotationSystemRef.current?.isObjectBeingDraggedAndRotated(objectID)) {
        console.log(`Skipping text update for ${objectID} - currently being rotated`);
        return;
    }

    if (scaleSystemRef.current?.isObjectBeingDraggedAndScaled(objectID)) {
        console.log(`Skipping text update for ${objectID} - currently being scaled`);
        return;
    }

    const objectData = objectsRef.current.get(objectID);
    if (!objectData || !objectData.mesh) return;

    // Get the current scene, camera, and renderer
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;
    const targetImageGroup = targetImageRef.current;

    // Clean up the old mesh
    if (objectData.cleanup) {
        objectData.cleanup();
    }
    // Render a new button with the updated properties
    try {
        const updatedButton = await renderObjectButton(scene, properties, objectID, targetImageGroup);

        if (updatedButton && updatedButton.mesh) {
            // Update the reference in objectsRef
            objectsRef.current.set(
                objectID,
                {
                    mesh: updatedButton.mesh,
                    cleanup: updatedButton.cleanup
                }
            );

            // Unlike image/video/social, button objects are torn down and rebuilt into a
            // brand new mesh on every properties update (canvas texture requires fresh
            // geometry), which drops any transform box attached to the old mesh. If this
            // object is currently selected with a transform tool active, reattach the box to
            // the new mesh so it doesn't silently disappear after drag/position updates.
            if (objectID === selectedObjectID && selectedTransformControlRef.current) {
                renderTransformBox(workspace, updatedButton.mesh);
            }

            const buttonWidth = updatedButton.mesh.userData.originalWidth;
            const buttonHeight = updatedButton.mesh.userData.originalHeight;
            if (properties.scaleX !== buttonWidth || properties.scaleY !== buttonHeight) {
                dispatch(updateObjectInTargetImage({
                    targetImageID: selectedTargetImageID ?? '',
                    object: {
                        id: objectID,
                        type: ObjectTypes.BUTTON,
                        properties: {
                            ...properties,
                            scaleX: buttonWidth,
                            scaleY: buttonHeight
                        }
                    },
                    skipHistory: true
                }))
                dispatch(setIsUpdatingFromEditorWorkspace(true))
            }

            renderer.render(scene, camera);
        } else {
            console.log("Failed to update object button");
        }
    } catch (error) {
        console.log("Error updating object button:", error);
    }
}

export const renderObjectButton = (
    scene: THREE.Scene,
    buttonProps: IObject,
    objectID: string,
    targetImageGroup: THREE.Group | null
): Promise<{ mesh: THREE.Group, cleanup: () => void } | null> => {

    return new Promise((resolve) => {
        const {
            presets, fontSize, fontFamily, text, fontStyle, fontColor, bgColor,
            positionX, positionY, positionZ, rotationX, renderOrder
        } = buttonProps;

        // The object's own colours win, with the preset supplying only a fallback -- identical
        // to renderObjectButtonAR in ar.screen.tsx, which is the contract that matters: it is
        // what the published experience actually paints, so anything else makes the editor
        // disagree with what the author is designing for.
        //
        // Previously these came from component-lifetime refs (prevObjBtnPresetRef and friends)
        // that encoded "what changed since the previous render call". On a fresh Edit load
        // there is no previous call -- prevObjBtnPresetRef.current was "" -- so
        // presetsAreEqual("", presets) evaluated `"" === presets`, i.e. false, which the code
        // read as "the preset just changed" and used to overwrite the painted colours with the
        // preset's own scheme, discarding the saved bgColor/fontColor that Redux and the
        // Properties panel were both still showing. It then stayed wrong: the bgColor guard
        // compared against a ref already holding the saved value, so re-picking that same
        // colour in the panel was a no-op and only a detour through a different colour could
        // dislodge it. The refs were also component-wide rather than keyed by objectID, so one
        // button was diffed against whatever the previously rendered button left behind.
        const textColor = fontColor || (presets?.color) || "#000000";
        const backgroundColor = bgColor || (presets?.backgroundColor) || "#FFFFFF";
        const borderRadius = presets?.borderRadius || 0;
        const borderWidth = presets?.borderWidth || 0;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
            console.error("Failed to get 2D context");
            resolve(null);
            return;
        }

        const baseFontSize = fontSize || 12;
        const scaledFontSize = baseFontSize;
        const isBold = fontStyle && fontStyle.includes("bold");
        const isItalic = fontStyle && fontStyle.includes("italic");
        const isUnderline = presets?.textDecoration || fontStyle && fontStyle.includes("underline");

        let fontStyleString = "";
        if (isBold) fontStyleString += "bold ";
        if (isItalic) fontStyleString += "italic ";

        const family = typeof getFontFamily === 'function'
            ? getFontFamily(fontFamily, fontStyle)
            : (fontFamily || "Arial");

        ctx.font = `${fontStyleString}${scaledFontSize}px ${family}`;

        const textMetrics = ctx.measureText(text);
        const textWidth = textMetrics.width;

        const paddingHorizontalRatio = 1.3;
        const paddingVerticalRatio = .67;

        const paddingHorizontal = (scaledFontSize * paddingHorizontalRatio);
        const paddingVertical = (scaledFontSize * paddingVerticalRatio);

        const buttonWidth = textWidth + (paddingHorizontal * 2);
        const buttonHeight = scaledFontSize + (paddingVertical * 2);

        const scale = 2;
        canvas.width = buttonWidth * scale;
        canvas.height = buttonHeight * scale;
        ctx.scale(scale, scale);

        ctx.font = `${fontStyleString}${scaledFontSize}px ${family}`;

        ctx.fillStyle = backgroundColor;

        if (typeof ctx.roundRect === 'function') {
            ctx.beginPath();
            ctx.roundRect(0, 0, buttonWidth, buttonHeight, borderRadius);
            ctx.fill();
        } else {
            drawRoundedRect(ctx, 0, 0, buttonWidth, buttonHeight, borderRadius);
        }

        if (borderWidth > 0) {
            ctx.strokeStyle = textColor;
            ctx.lineWidth = borderWidth;

            const halfBorder = borderWidth / 2;
            const borderAdjustment = borderWidth;

            if (typeof ctx.roundRect === 'function') {
                ctx.beginPath();
                ctx.roundRect(
                    halfBorder,
                    halfBorder,
                    buttonWidth - borderAdjustment,
                    buttonHeight - borderAdjustment,
                    borderRadius
                );
            } else {
                drawRoundedRect(
                    ctx,
                    halfBorder,
                    halfBorder,
                    buttonWidth - borderAdjustment,
                    buttonHeight - borderAdjustment,
                    borderRadius
                );
            }

            ctx.lineJoin = "round";
            ctx.stroke();
        }

        ctx.fillStyle = textColor;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(text, buttonWidth / 2, buttonHeight / 2);

        if (isUnderline) {
            const underlineY = buttonHeight / 2 + scaledFontSize / 2;

            ctx.beginPath();
            ctx.moveTo(buttonWidth / 2 - textWidth / 2, underlineY);
            ctx.lineTo(buttonWidth / 2 + textWidth / 2, underlineY);
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = textColor;
            ctx.stroke();
        }

        const parentObject = targetImageGroup || scene;

        const buttonGroup = new THREE.Group();
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        const buttonGeometry = new THREE.PlaneGeometry(1, 1);
        const buttonMaterial = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
        });

        const buttonMesh = new THREE.Mesh(buttonGeometry, buttonMaterial);
        buttonMesh.renderOrder = renderOrder || 0;
        buttonMesh.scale.set(buttonWidth, buttonHeight, 1);

        buttonGroup.add(buttonMesh);

        buttonGroup.position.set(positionX || 0, positionY || 0, positionZ || 0);
        const rotationXRadians = THREE.MathUtils.degToRad(rotationX || 0);
        buttonGroup.rotation.set(0, 0, rotationXRadians);

        // Previously this blindly reattached transformBoxRef.current (a single,
        // component-wide "last created box" ref, not scoped to this object) onto every new
        // button mesh whenever any box happened to exist elsewhere in the scene, falsely
        // marking it as "already has a box" and skipping real per-object box creation. Box
        // reattachment for the currently selected+tooled object is handled by the
        // selection-driven effect instead — same fix already applied to text objects.

        const existingChild = parentObject.children.find(child => child.userData.id === objectID);
        if (existingChild) {
            parentObject.remove(existingChild);
        }
        parentObject.add(buttonGroup);

        // renderOrder above was only ever set on the inner buttonMesh — the top-level scene
        // sort (targetImageRef.current.children.sort by renderOrder) sorts on buttonGroup
        // itself, which every other object type (image/video/social/text) sets renderOrder on
        // but this one never did, leaving it stuck at THREE.Object3D's default of 0 regardless
        // of the button's actual z-order.
        buttonGroup.renderOrder = renderOrder || 0;
        buttonGroup.userData.isDraggable = true;
        buttonGroup.userData.id = objectID;
        buttonGroup.userData.originalWidth = buttonWidth;
        buttonGroup.userData.originalHeight = buttonHeight;
        buttonGroup.userData.objectType = ObjectTypes.BUTTON;
        buttonGroup.userData.prevProperties = { ...buttonProps };
        buttonGroup.userData.renderOrder = renderOrder || 0;

        const cleanup = () => {
            if (parentObject) {
                const childToRemove = parentObject.children.find(child => child.userData.id === objectID);
                if (childToRemove) {
                    parentObject.remove(childToRemove);
                }
            }

            if (buttonGeometry) buttonGeometry.dispose();

            if (buttonMaterial) {
                if (Array.isArray(buttonMaterial)) {
                    buttonMaterial.forEach(material => {
                        if (material.map) material.map.dispose();
                        material.dispose();
                    });
                } else {
                    if (buttonMaterial.map) buttonMaterial.map.dispose();
                    buttonMaterial.dispose();
                }
            }

            if (texture) texture.dispose();

            // The transform box lives as a sibling in sceneRef.current, not nested inside
            // buttonGroup — wiping userData below only drops the reference to it, leaving it
            // as a permanently orphaned, undisposed ghost box unless removed explicitly here.
            if (buttonGroup.userData.transformBox) {
                buttonGroup.userData.transformBox.removeFromParent();
                buttonGroup.userData.transformBox.traverse((child: THREE.Object3D) => {
                    if (child instanceof Line2) {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) child.material.dispose();
                    }
                });
            }

            buttonGroup.userData = {};
        };

        function drawRoundedRect(
            ctx: CanvasRenderingContext2D,
            x: number,
            y: number,
            width: number,
            height: number,
            radius: number
        ) {
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + width - radius, y);
            ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
            ctx.lineTo(x + width, y + height - radius);
            ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
            ctx.lineTo(x + radius, y + height);
            ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
            ctx.lineTo(x, y + radius);
            ctx.quadraticCurveTo(x, y, x + radius, y);
            ctx.closePath();
            ctx.fill();
        }

        const loadAndRender = () => {
            if (typeof FontFaceObserver !== 'undefined') {
                const font = new FontFaceObserver(family);
                font.load(null, 5000)
                    .then(() => {
                        console.log("Font loaded successfully:", family);
                        resolve({ mesh: buttonGroup, cleanup });
                    })
                    .catch(() => {
                        console.warn("Font failed to load:", family);
                        resolve({ mesh: buttonGroup, cleanup });
                    });
            } else {
                resolve({ mesh: buttonGroup, cleanup });
            }
        };

        loadAndRender();
    });
};
